import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import FileUploadDropzone from "./FileUploadDropzone";
import { Button } from "./ui/button";
import { ShieldCheck, BadgeCheck, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const KIND_KEYS = ["national_id", "business_registration", "proof_of_address"];

const STATUS_META = {
  pending:   { icon: AlertCircle, key: "kyc_pending",   color: "text-[#5c6360]",   badge: "badge-pending" },
  in_review: { icon: Clock,       key: "kyc_in_review", color: "text-[#8a5a16]",   badge: "badge-review" },
  approved:  { icon: BadgeCheck,  key: "kyc_approved",  color: "text-[#114232]",   badge: "badge-success" },
  rejected:  { icon: AlertCircle, key: "kyc_rejected",  color: "text-[#c15337]",   badge: "badge-rejected" },
};

export default function KycCard({ merchant, onSubmitted }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState("national_id");
  const [busy, setBusy] = useState(false);

  function load() {
    api.get("/merchant/kyc-documents").then((r) => setDocs(r.data.documents || []));
  }
  useEffect(load, []);

  async function submit() {
    if (!files[0]) return;
    setBusy(true);
    try {
      await api.post("/merchant/kyc-document", { file_id: files[0].id, kind });
      toast.success(t("merchant_dashboard.kyc_in_review"));
      setFiles([]);
      load();
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  const status = merchant?.kyc_status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const StatusIcon = meta.icon;

  return (
    <div className="metric-card" data-testid="kyc-card">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-[#114232]" /> {t("merchant_dashboard.kyc_title")}
        </div>
        <span className={`badge-status ${meta.badge} inline-flex items-center gap-1`} data-testid="kyc-status-badge">
          <StatusIcon className="w-3 h-3" /> {t(`merchant_dashboard.${meta.key}`)}
        </span>
      </div>
      <p className="text-xs text-[#5c6360] mt-2">{t("merchant_dashboard.kyc_subtitle")}</p>

      {status !== "approved" && (
        <div className="mt-4 space-y-3" data-testid="kyc-submit-form">
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#5c6360]">
            {t("merchant_dashboard.kyc_select_kind")}
          </label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="kyc-kind-select"
            className="w-full h-9 px-3 rounded-md border border-[#114232]/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#114232]/20"
          >
            {KIND_KEYS.map((k) => (
              <option key={k} value={k}>{t(`merchant_dashboard.kyc_doc_${k}`)}</option>
            ))}
          </select>
          <FileUploadDropzone files={files} setFiles={setFiles} purpose="kyc_document" />
          <Button
            onClick={submit}
            disabled={!files[0] || busy}
            data-testid="kyc-submit-btn"
            className="w-full btn-baobab rounded-full hover:bg-[var(--baobab-hover)]"
          >
            {t("merchant_dashboard.kyc_submit")}
          </Button>
        </div>
      )}

      {docs.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-xs" data-testid="kyc-doc-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between border-t border-[#114232]/10 pt-1.5">
              <span className="capitalize">{d.kind.replace(/_/g, " ")}</span>
              <span className="text-[#5c6360] font-mono">{new Date(d.submitted_at).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
